export interface RegionNode {
  value: string;
  key: string;
  title: string;
  label: string;
  isoCode?: string;
  children?: RegionNode[];
}

function kebab(str: string): string {
  return str.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

type RawItem = string | [string, string];
type RawTree = Record<string, (RawItem | Record<string, RawItem[]>)[]>;

function toRec(name: RawItem, parent = ''): RegionNode {
  let label: string;
  let code: string | undefined;
  if (Array.isArray(name)) {
    [label, code] = name;
  } else {
    label = name;
  }
  const val = kebab(`${parent} ${label}`.trim());
  return { value: val, key: val, title: label, label, isoCode: code?.toUpperCase() };
}

function toTree(obj: RawTree): RegionNode[] {
  return Object.entries(obj).map(([name, children]) => {
    const rec = toRec(name);
    rec.children = children.map((c) => {
      if (typeof c === 'string' || Array.isArray(c)) return toRec(c, name);
      const [subName, subChildren] = Object.entries(c)[0];
      const sub = toRec(subName, name);
      sub.children = subChildren.map((sc) => toRec(sc, `${name} ${subName}`));
      return sub;
    });
    return rec;
  });
}

export const regionsTree: RegionNode[] = toTree({
  Asia: [
    { 'East Asia': [['China', 'CN'], ['Japan', 'JP'], ['Mongolia', 'MN'], ['North Korea', 'KP'], ['South Korea', 'KR'], ['Taiwan', 'TW'], ['Hong Kong', 'HK'], ['Macao', 'MO']] },
    { 'South Asia': [['Afghanistan', 'AF'], ['Bangladesh', 'BD'], ['Bhutan', 'BT'], ['India', 'IN'], ['Maldives', 'MV'], ['Nepal', 'NP'], ['Pakistan', 'PK'], ['Sri Lanka', 'LK']] },
    { 'Southeast Asia': ['Brunei', ['Cambodia', 'KH'], ['Indonesia', 'ID'], ['Laos', 'LA'], ['Malaysia', 'MY'], 'Myanmar', ['Philippines', 'PH'], ['Singapore', 'SG'], ['Thailand', 'TH'], 'Timor Leste', ['Vietnam', 'VN']] },
    { 'West Asia': ['Abkhazia', ['Armenia', 'AM'], 'Artsakh', ['Azerbaijan', 'AZ'], ['Bahrain', 'BH'], ['Cyprus', 'CY'], ['Georgia', 'GE'], ['Iran', 'IR'], ['Iraq', 'IQ'], ['Israel', 'IL'], ['Jordan', 'JO'], ['Kuwait', 'KW'], ['Lebanon', 'LB'], ['Oman', 'OM'], 'Palestine', ['Qatar', 'QA'], ['Saudi Arabia', 'SA'], 'South Ossetia', 'Syria', ['Turkey', 'TR'], ['UAE', 'AE'], ['Yemen', 'YE']] },
    { 'Central Asia': [['Kazakhstan', 'KZ'], ['Kyrgyzstan', 'KG'], ['Tajikistan', 'TJ'], ['Turkmenistan', 'TM'], ['Uzbekistan', 'UZ']] },
  ],
  Europe: [
    ['Albania', 'AL'], ['Andorra', 'AD'], ['Armenia', 'AM'], ['Austria', 'AT'], ['Azerbaijan', 'AZ'],
    ['Belarus', 'BY'], ['Belgium', 'BE'], ['Bosnia and Herzegovina', 'BA'], ['Bulgaria', 'BG'], ['Croatia', 'HR'],
    ['Cyprus', 'CY'], ['Czech Republic', 'CZ'], ['Denmark', 'DK'], ['Estonia', 'EE'], ['Finland', 'FI'],
    ['France', 'FR'], ['Georgia', 'GE'], ['Germany', 'DE'], ['Greece', 'GR'], ['Hungary', 'HU'],
    ['Iceland', 'IS'], ['Ireland', 'IE'], ['Italy', 'IT'], 'Kosovo', ['Latvia', 'LV'],
    ['Liechtenstein', 'LI'], ['Lithuania', 'LT'], ['Luxembourg', 'LU'], ['Macedonia', 'MK'], ['Malta', 'MT'],
    'Moldova', ['Monaco', 'MC'], ['Montenegro', 'ME'], ['Netherlands', 'NL'], ['Norway', 'NO'],
    ['Poland', 'PL'], ['Portugal', 'PT'], ['Romania', 'RO'], ['Russia', 'RU'], ['San Marino', 'SM'],
    ['Serbia', 'RS'], ['Slovakia', 'SK'], ['Slovenia', 'SI'], ['Spain', 'ES'], ['Sweden', 'SE'],
    ['Switzerland', 'CH'], ['Turkey', 'TR'], ['Ukraine', 'UA'], ['United Kingdom', 'GB'], 'Vatican City',
  ],
  'North America': [['US', 'US'], ['Canada', 'CA']],
  'Latin America': [
    ['Mexico', 'MX'], ['Belize', 'BZ'], ['Costa Rica', 'CR'], ['El Salvador', 'SV'], ['Guatemala', 'GT'],
    ['Honduras', 'HN'], ['Nicaragua', 'NI'], ['Panama', 'PA'], ['Argentina', 'AR'], ['Bolivia', 'BO'],
    ['Brazil', 'BR'], ['Chile', 'CL'], ['Colombia', 'CO'], ['Ecuador', 'EC'], ['Guyana', 'GY'],
    ['Paraguay', 'PY'], ['Peru', 'PE'], ['Suriname', 'SR'], ['Uruguay', 'UY'], ['Venezuela', 'VE'],
    ['Antigua and Barbuda', 'AG'], ['Aruba', 'AW'], ['Bahamas', 'BS'], ['Barbados', 'BB'], ['Cuba', 'CU'],
    ['Dominica', 'DM'], ['Grenada', 'GD'], ['Haiti', 'HT'], ['Jamaica', 'JM'], ['Puerto Rico', 'PR'],
  ],
  Australia: [['Australia', 'AU']],
});

/** kebab-case value → 사람이 읽을 수 있는 라벨로 변환 */
function buildLabelMap(nodes: RegionNode[]): Map<string, string> {
  const map = new Map<string, string>();
  const walk = (list: RegionNode[]) => {
    for (const n of list) {
      map.set(n.value, n.title);
      if (n.children) walk(n.children);
    }
  };
  walk(nodes);
  return map;
}

const regionLabelMap = buildLabelMap(regionsTree);

export function getRegionLabel(value: string): string {
  return regionLabelMap.get(value) || value.split('-').map((w) => w[0]?.toUpperCase() + w.slice(1)).join(' ');
}
